import { useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";

const STAGE_COLORS = {
  "Queued": "bg-slate-100 text-slate-700",
  "Kickoff": "bg-sky-100 text-sky-700",
  "Production": "bg-amber-100 text-amber-700",
  "Delivered": "bg-green-100 text-green-700",
  "Closed": "bg-zinc-200 text-zinc-600"
};

export function ProjectRow({ project, canSeeCosts, onNavigate }) {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="border rounded-lg overflow-hidden">
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-secondary/30 transition-colors">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Button variant="ghost" size="sm" className="p-0 h-auto hover:bg-transparent">
                {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
              </Button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-sm truncate">{project.program_name}</p>
                  {project.stage && (
                    <Badge className={`${STAGE_COLORS[project.stage] || 'bg-gray-100'} text-xs rounded-full`}>
                      {project.stage}
                    </Badge>
                  )}
                </div>
                {project.client_name && (
                  <p className="text-xs text-muted-foreground truncate">{project.client_name}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="text-right">
                <p className="font-semibold text-sm">{project.total_hours}h</p>
                {canSeeCosts && project.total_cost !== null && (
                  <p className="text-xs text-muted-foreground">${project.total_cost.toLocaleString()}</p>
                )}
              </div>
              <Button variant="ghost" size="sm" className="p-1 h-auto" onClick={(e) => { e.stopPropagation(); onNavigate(project.program_id); }}>
                <ExternalLink className="w-4 h-4 text-muted-foreground" />
              </Button>
            </div>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <div className="border-t bg-secondary/10 px-4 py-3">
            <p className="text-xs font-medium text-muted-foreground mb-2">Assignment Blocks</p>
            <div className="space-y-2">
              {project.blocks.map((block, idx) => (
                <div key={block.id || idx} className="flex items-center justify-between text-sm p-2 bg-background rounded-md">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-2 h-2 rounded-full bg-primary/50 flex-shrink-0" />
                    <span className="truncate">{block.title || block.work_type || "Work Block"}</span>
                    <span className="text-xs text-muted-foreground flex-shrink-0">{block.start_date} → {block.end_date}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="font-medium">{block.hours}h</span>
                    {canSeeCosts && block.cost !== null && <span className="text-muted-foreground text-xs">${block.cost}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
